/* YG — shared carousel init (Phase 1 shared component).
   One Swiper-backed shell reused by every carousel section (Category, Best
   Sellers, Testimonials, Bundles, Editorial, Blog) — only per-instance
   OPTIONS differ, not the component. Options are read from data-yg-carousel-*
   attributes set by the calling section's own schema settings; nothing here
   is section-specific.

   enabled_at maps 1:1 to the reference's own enabled:false->true breakpoint
   toggle (Category: desktop_only: enabled false at base, true at 990px.
   Blog: mobile_only: the exact inverse). Swiper's own breakpoints system
   handles the enable/disable transition natively.

   When Swiper is inactive at the current breakpoint, this adds
   [data-yg-carousel-disabled] to the root element — the ONE guaranteed hook
   the shell owns. The actual fallback layout (Category's plain wrapped grid
   vs Blog's asymmetric featured-spans-2-rows grid) is NOT shell behaviour —
   those are structurally different per source and belong to each section's
   own CSS targeting that attribute.

   autoHeight and centeredSlides are genuinely breakpoint-dependent in source
   (Editorial: centeredSlides true->false and autoHeight true->false at
   990px) — modelled the same way space_between/space_between_desktop
   already are: a mobile value + an optional desktop override, not a single
   static bool.

   Pagination (bullets + progress ring) ported verbatim from source, not
   invented: custom renderBullet (thumbnail image + two-circle SVG ring),
   driven by Swiper's own real autoplayTimeLeft event. Only used when a
   section explicitly asks for it (Editorial); every other instance omits
   the option entirely, at zero cost. Each slide's data-img must come from
   that section's own image_picker setting (never a path lifted from the
   reference) — when unset, renderBullet renders a placeholder icon instead
   of an empty ring, never a blank circle.
   Reference renders each bullet as a <div data-index tabindex="0"> — no
   aria-label, no button semantics, confirmed in its own captured markup.
   Reproduced here as a real <button> with a descriptive aria-label instead:
   these are real clickable navigation (the only navigation in this
   carousel — Editorial ships with arrows/scrollbar both off, confirmed
   display:none at every breakpoint in source), so they need to be reachable
   the same way as any other control, not just an improvement-for-its-own-
   sake. aria-current is kept in sync on every slideChange (Swiper renders
   each bullet's HTML once via renderBullet and thereafter only toggles the
   -active CLASS itself on slide change — it does not re-run renderBullet or
   touch aria-current, so that half has to be done here explicitly).

   AUTOPLAY / PAUSE MECHANISM — reference's own config
   (lovewellness.html:12757-12763) is exactly:
     { "delay": 5000, "waitForTransition": false, "disableOnInteraction": false }
   waitForTransition:false is reproduced verbatim (hardcoded below, not
   exposed as an option — nothing in source varies it). disableOnInteraction
   is reproduced as `false` in source, meaning even manually dragging the
   carousel does NOT stop autoplay — confirmed by reading both nss-carousel.js
   and the companion nss-clinical-studies-value-prop.js in full: neither
   contains any pause/hover/focus/mouseenter handling at all. The reference
   ships an auto-advancing carousel with genuinely no way to stop it, a real
   WCAG 2.2.2 (Level A) gap in the source itself. Deliberately NOT reproduced:
     - disableOnInteraction flipped to true here (an interaction permanently
       stops autoplay, rather than silently resuming).
     - pauseOnMouseEnter:true added (Swiper's own module option) — covers
       mouse users.
     - focusin/focusout on the whole carousel calls swiper.autoplay.stop()/
       start() — covers keyboard users tabbing to the arrows/pagination,
       which pauseOnMouseEnter alone does not.
     - prefers-reduced-motion:reduce removes autoplay entirely (not just
       speed:0) — auto-advancing slides are exactly the motion that setting
       exists to suppress. Touch users are covered by disableOnInteraction:
       true, since starting a swipe counts as the interaction that stops it
       for good. */
(function () {
  'use strict';

  function readData(root) {
    var d = root.dataset;
    return {
      enabledAt: d.ygCarouselEnabledAt || 'always',
      arrows: d.ygCarouselArrows !== 'false',
      arrowsDesktop: d.ygCarouselArrowsDesktop !== 'false',
      scrollbar: d.ygCarouselScrollbar !== 'false',
      scrollbarDesktop: d.ygCarouselScrollbarDesktop !== 'false',
      drag: d.ygCarouselDrag !== 'false',
      effect: d.ygCarouselEffect || 'slide',
      autoplayDelay: d.ygCarouselAutoplayDelay ? parseInt(d.ygCarouselAutoplayDelay, 10) : null,
      slidesPerView: d.ygCarouselSlidesPerView || 'auto',
      spaceBetween: d.ygCarouselSpaceBetween ? parseInt(d.ygCarouselSpaceBetween, 10) : 12,
      spaceBetweenDesktop: d.ygCarouselSpaceBetweenDesktop ? parseInt(d.ygCarouselSpaceBetweenDesktop, 10) : 16,
      centeredSlides: d.ygCarouselCenteredSlides === 'true',
      centeredSlidesDesktop: d.ygCarouselCenteredSlidesDesktop === 'true',
      autoHeight: d.ygCarouselAutoHeight === 'true',
      autoHeightDesktop: d.ygCarouselAutoHeightDesktop === 'true',
      loop: d.ygCarouselLoop === 'true',
      pagination: d.ygCarouselPagination === 'true'
    };
  }

  var placeholderIcon = '<span class="yg-carousel__bullet-placeholder" aria-hidden="true">' +
    '<svg viewBox="0 0 24 24" width="24" height="24" fill="none"><rect x="2.5" y="4.5" width="19" height="15" rx="2" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="10" r="1.75" stroke="currentColor" stroke-width="1.5"/><path d="M21 15.5L15.5 11 6 19" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>' +
    '</span>';

  /* Equal-height slides, done in JS, not left to CSS align-items:stretch —
     confirmed live that stretch does NOT equalise slides here despite
     several callers' own comments assuming it does (Best Sellers,
     Testimonials): this wrapper's cross-size is itself content-derived
     (height:100% of an equally auto-height ancestor, never a definite
     value), and CSS forbids align-items:stretch from having anything to
     stretch INTO when the container's own cross-size is auto — a real
     circular-dependency limit of the spec, not a bug in one property.
     Real Swiper.js's own bundled CSS likely papers over this same case;
     this project vendors Swiper's JS only, not its CSS (see file header),
     so that part was never ported. Confirmed with Best Sellers' own real
     content: its 4 cards only ever LOOKED equal because their tagline/
     rating combination happened to occupy near-identical space — the
     mechanism was never actually verified working until Bundles' own
     variable-length titles exposed it. Skipped entirely when auto_height/
     auto_height_desktop is requested (Editorial) — that's a deliberate
     per-slide natural height, equalising would fight it. */
  function equalizeSlideHeights(root, opts) {
    if (opts.autoHeight || opts.autoHeightDesktop) return;
    var wrapper = root.querySelector('[data-yg-carousel-wrapper]');
    if (!wrapper) return;
    var slides = Array.prototype.filter.call(wrapper.children, function (el) {
      return el.classList.contains('swiper-slide');
    });
    /* Bail (and clear any stale height from the LAST breakpoint where the
       carousel WAS active) once Swiper is disabled at the current
       breakpoint — syncDisabledAttr has already set [data-yg-carousel-
       disabled] on root by the time this runs (called right after it in
       both the init and breakpoint handlers below). Once disabled, the
       caller's own CSS owns sizing for its fallback layout (Category's
       wrapped 2-col grid, Blog's asymmetric featured-spans-2-rows grid) —
       this function has no business touching it. Missing this check was a
       real, confirmed, shell-level bug, not section-specific: every Blog
       slide carried a stale inline height (620px, equalized against each
       OTHER at the last MOBILE measurement, before the breakpoint flipped
       to desktop and syncDisabledAttr fired) that silently beat a
       302px/302px grid-template-rows fix in Blog's own CSS, since an
       inline style wins over an external stylesheet regardless of
       specificity — confirmed live via each slide's own style attribute,
       not guessed. Any other enabled_at:'desktop_only'/'mobile_only'
       section relying on its own disabled-state row/height CSS was
       equally exposed; this fixes all of them at once, not just Blog. */
    if (root.hasAttribute('data-yg-carousel-disabled')) {
      slides.forEach(function (s) { s.style.height = ''; });
      return;
    }
    if (slides.length < 2) return;
    slides.forEach(function (s) { s.style.height = ''; });
    var maxHeight = 0;
    slides.forEach(function (s) { maxHeight = Math.max(maxHeight, s.getBoundingClientRect().height); });
    slides.forEach(function (s) { s.style.height = maxHeight + 'px'; });
  }

  // Trackpad / wheel scrolling.
  //
  // Swiper's own Mousewheel module is NOT in this build — assets/yg-swiper.min.js
  // is a custom bundle of Navigation, Scrollbar, A11y, EffectFade, Autoplay and
  // Pagination only. Passing it a `mousewheel` config does nothing at all: the
  // key is read by code that isn't there, so it fails silently. This does the
  // work directly instead of re-bundling a vendor file for one feature.
  //
  // A trackpad reports a two-finger swipe as deltaX (horizontal) and deltaY
  // (vertical). A horizontal carousel should answer to the horizontal one --
  // but a plain mouse has no deltaX at all, only a vertical wheel. So take
  // whichever axis the gesture is actually dominated by, and only claim the
  // gesture when it is horizontal, or when there is no horizontal axis to read.
  // That keeps a vertical two-finger scroll over a carousel scrolling the PAGE,
  // which is what a reader expects, rather than being swallowed sideways.
  function attachWheel(root, el, swiper) {
    var lastEnd = 0;

    el.addEventListener('wheel', function (e) {
      if (!swiper.enabled || swiper.destroyed) return;

      var x = e.deltaX;
      var y = e.deltaY;

      // Nothing sideways in the gesture: a plain vertical scroll, or a mouse
      // wheel. Leave it to the page -- a reader scrolling past a carousel
      // expects the page to keep moving, not to be caught by it.
      if (x === 0) return;

      // Claim the gesture BEFORE any threshold test. macOS reads a sideways
      // trackpad swipe as a back/forward navigation, and it builds that from
      // the whole stream -- including the 1-2px deltas a swipe opens with.
      // Returning early on those let them through and the browser still left
      // the page, which is what the previous attempt here got wrong: it
      // guarded the ends but not the small deltas. Nothing horizontal over a
      // carousel should ever reach the browser, so preventDefault comes first
      // and every later return is only about whether to MOVE the carousel.
      //
      // (overscroll-behavior-x cannot do this job: it applies to a scroll
      // container, and the Swiper wrapper does not overflow horizontally.)
      if (Math.abs(x) > Math.abs(y)) e.preventDefault();

      // Trackpad noise rather than intent. Deliberately compared against x
      // alone: a real horizontal swipe on a Mac carries a wobble of vertical
      // drift, and requiring |x| to beat |y| threw those away, so swiping one
      // direction often did nothing at all.
      if (Math.abs(x) < 12) return;
      if (Math.abs(x) < Math.abs(y)) return;

      if ((x < 0 && swiper.isBeginning) || (x > 0 && swiper.isEnd)) return;

      // One slide per gesture. A trackpad emits a long tail of events for a
      // single flick, which would otherwise run the carousel to its end.
      var now = Date.now();
      if (now - lastEnd < 300) return;
      lastEnd = now;

      if (x > 0) swiper.slideNext();
      else swiper.slidePrev();
    }, { passive: false });
  }

  function initCarousel(root) {
    var el = root.querySelector('[data-yg-carousel-swiper]');
    if (!el || el.__ygSwiperInit || !window.YGSwiper) return;
    el.__ygSwiperInit = true;

    var opts = readData(root);
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var config = {
      loop: opts.loop,
      centeredSlides: opts.centeredSlides,
      slidesPerView: opts.slidesPerView,
      spaceBetween: opts.spaceBetween,
      direction: 'horizontal',
      autoHeight: opts.autoHeight,
      grabCursor: opts.drag,
      simulateTouch: opts.drag,
      allowTouchMove: opts.drag,
      a11y: { enabled: true },
      breakpoints: {
        990: {
          loop: opts.loop,
          autoHeight: opts.autoHeightDesktop,
          centeredSlides: opts.centeredSlidesDesktop,
          slidesPerView: opts.slidesPerView,
          spaceBetween: opts.spaceBetweenDesktop,
          direction: 'horizontal'
        }
      }
    };

    // Registered whenever wanted at EITHER breakpoint — actual per-breakpoint
    // show/hide is pure CSS (see yg-carousel.css), so the underlying Swiper
    // module just needs to exist and stay functional whenever its DOM is
    // visible at all, not track the breakpoint itself.
    if (opts.arrows || opts.arrowsDesktop) {
      config.navigation = {
        prevEl: root.querySelector('[data-yg-carousel-prev]'),
        nextEl: root.querySelector('[data-yg-carousel-next]')
      };
    }
    if (opts.scrollbar || opts.scrollbarDesktop) {
      config.scrollbar = {
        el: root.querySelector('[data-yg-carousel-scrollbar]'),
        draggable: true
      };
    }
    if (opts.effect === 'fade') {
      config.effect = 'fade';
    }
    if (opts.autoplayDelay && !reduceMotion) {
      config.autoplay = {
        delay: opts.autoplayDelay,
        waitForTransition: false, // matches source verbatim
        disableOnInteraction: true, // DEVIATION from source's false — see file header
        pauseOnMouseEnter: true // addition, not in source — see file header
      };
    }
    if (opts.pagination) {
      var paginationEl = root.querySelector('[data-yg-carousel-pagination]');
      config.pagination = {
        el: paginationEl,
        type: 'bullets',
        clickable: true,
        renderBullet: function (index, className) {
          var slide = el.querySelectorAll('.swiper-slide')[index];
          var img = slide ? slide.getAttribute('data-img') : '';
          var heading = slide ? slide.getAttribute('data-heading') : '';
          var media = img ? '<img alt="" src="' + img + '">' : placeholderIcon;
          var ring =
            '<svg width="100%" height="100%" viewBox="0 0 100 100">' +
            '<circle class="yg-carousel__ring-bg" cx="50" cy="50" r="49" fill="transparent" stroke-width="1.5"></circle>' +
            '<circle class="yg-carousel__ring-progress js-progress" cx="50" cy="50" r="49" fill="transparent" stroke-width="1.5" pathLength="100" stroke-dasharray="100 100"></circle>' +
            '</svg>';
          var label = heading ? 'Go to slide ' + (index + 1) + ': ' + heading : 'Go to slide ' + (index + 1);
          var active = className.indexOf('swiper-pagination-bullet-active') !== -1;
          return '<button type="button" class="' + className + '" data-index="' + index + '" aria-label="' +
            label.replace(/"/g, '&quot;') + '"' + (active ? ' aria-current="true"' : '') + '>' + media + ring + '</button>';
        }
      };
    }
    if (opts.enabledAt === 'desktop_only') {
      config.enabled = false;
      config.breakpoints[990].enabled = true;
    } else if (opts.enabledAt === 'mobile_only') {
      config.enabled = true;
      config.breakpoints[990].enabled = false;
    }
    if (reduceMotion) {
      config.speed = 0;
    }

    function syncDisabledAttr(swiper) {
      if (swiper.enabled) root.removeAttribute('data-yg-carousel-disabled');
      else root.setAttribute('data-yg-carousel-disabled', '');
    }

    config.on = {
      init: function () { syncDisabledAttr(this); equalizeSlideHeights(root, opts); root.classList.add('yg-carousel--ready'); },
      breakpoint: function () { syncDisabledAttr(this); equalizeSlideHeights(root, opts); }
    };
    if (opts.pagination) {
      config.on.autoplayTimeLeft = function (swiperInst, timeLeft, progress) {
        var pct = (1 - progress) * 100;
        root.querySelectorAll('.swiper-pagination-bullet-active .js-progress').forEach(function (ring) {
          ring.setAttribute('stroke-dasharray', pct + ' 100');
        });
      };
      config.on.slideChange = function () {
        root.querySelectorAll('.js-progress').forEach(function (ring) {
          ring.setAttribute('stroke-dasharray', '100 100');
        });
        root.querySelectorAll('.swiper-pagination-bullet').forEach(function (bullet) {
          if (bullet.classList.contains('swiper-pagination-bullet-active')) bullet.setAttribute('aria-current', 'true');
          else bullet.removeAttribute('aria-current');
        });
      };
    }

    var swiper = new window.YGSwiper(el, config);
    // Exposed so section-specific scripts (e.g. yg-home-categories.js, which
    // toggles a slide's own width on hover/click) can tell Swiper to
    // re-measure after that width transition finishes.
    root.__ygSwiper = swiper;

    // Off for a fade carousel, which has no axis to scroll along, and off
    // wherever dragging is disabled -- a carousel the merchant locked to its
    // arrows should not be scrollable by wheel either.
    if (opts.drag && opts.effect !== 'fade') {
      attachWheel(root, el, swiper);
    }

    // Web fonts (Outfit/Inter/Cormorant) swap in asynchronously after first
    // paint and can shift text-wrapping/line-count — re-measure once they're
    // actually loaded so the equalised height reflects final metrics, not
    // whatever the fallback font measured at init.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { equalizeSlideHeights(root, opts); });
    }

    // Keyboard-user pause: pauseOnMouseEnter only covers mouse hover, so
    // tabbing into the carousel's own controls must pause it independently.
    if (config.autoplay && swiper.autoplay) {
      root.addEventListener('focusin', function () { swiper.autoplay.stop(); });
      root.addEventListener('focusout', function (e) {
        if (!root.contains(e.relatedTarget)) swiper.autoplay.start();
      });
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-yg-carousel-root]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
